import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, Button, Card, Tooltip } from 'antd';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import ScholarSidebar from '../components/ScholarSidebar';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import '../styles/ScholarAcknowledgementReceipt.css';
import ToastMessage from '../components/ToastMessage';

const { Content } = Layout;
const { Title, Text } = Typography;

const ScholarAcknowledgementDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const receipt = location.state?.transaction;
    const [scholarName, setScholarName] = useState('');
    const [isForSignature, setIsForSignature] = useState(false);
    const [isSigned, setIsSigned] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 375);
    const [isExtraSmallMobile, setIsExtraSmallMobile] = useState(window.innerWidth <= 320);

    // Format date to show only Month day, year
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Track mobile state for responsive UI
    useEffect(() => {
        const handleWindowResize = () => {
            setIsMobile(window.innerWidth <= 425);
            setIsSmallMobile(window.innerWidth <= 375);
            setIsExtraSmallMobile(window.innerWidth <= 320);
        };
        
        window.addEventListener('resize', handleWindowResize);
        
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    useEffect(() => {
        // Get scholar name components from localStorage
        const first_name = localStorage.getItem('first_name') || '';
        const middle_name = localStorage.getItem('middle_name') || '';
        const last_name = localStorage.getItem('last_name') || '';
        const suffix = localStorage.getItem('suffix') || '';

        // Construct full name
        const fullName = [
            first_name,
            middle_name,
            last_name,
            suffix
        ].filter(Boolean).join(' ').trim();

        if (fullName) {
            setScholarName(fullName);
        } else {
            // Fallback to receipt scholar name if localStorage data is not available
            setScholarName(receipt?.scholarName || 'Scholar Name Not Found');
        }

        // Check if receipt status is "For Signature" or "Signed"
        if (receipt && receipt.status) {
            const status = receipt.status.toLowerCase();
            setIsForSignature(status === 'for signature');
            setIsSigned(status === 'signed');
        }
    }, [receipt]);

    useEffect(() => {
        if (!isForSignature || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        
        // Set canvas size to match display size * device pixel ratio
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        
        const context = canvas.getContext("2d", {
            willReadFrequently: true,
            alpha: false // Better performance with no alpha
        });
        
        // Fill canvas with white background
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.scale(dpr, dpr);
        context.lineCap = "round";
        context.lineJoin = "round";
        context.strokeStyle = "black";
        
        // Adjust line width based on device screen size for better mobile experience
        if (window.innerWidth <= 425) {
            context.lineWidth = 2.5; // Slightly thinner line for smaller screens
        } else {
            context.lineWidth = 3;
        }
        
        contextRef.current = context;

        // Handle window resize
        const handleResize = () => {
            const newRect = canvas.getBoundingClientRect();
            canvas.width = newRect.width * dpr;
            canvas.height = newRect.height * dpr;
            
            // Restore context settings after resize
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.scale(dpr, dpr);
            context.lineCap = "round";
            context.lineJoin = "round";
            context.strokeStyle = "black";
            
            // Adjust line width based on device screen size
            if (window.innerWidth <= 425) {
                context.lineWidth = 2.5;
            } else {
                context.lineWidth = 3;
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isForSignature]);

    // Touch event handlers
    const handleStart = useCallback((x, y) => {
        if (!contextRef.current) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvasRef.current.getBoundingClientRect();
        const offsetX = (x - rect.left) / dpr;
        const offsetY = (y - rect.top) / dpr;
        
        contextRef.current.beginPath();
        contextRef.current.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    }, []);

    const handleMove = useCallback((x, y) => {
        if (!isDrawing || !contextRef.current) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvasRef.current.getBoundingClientRect();
        const offsetX = (x - rect.left) / dpr;
        const offsetY = (y - rect.top) / dpr;
        
        contextRef.current.lineTo(offsetX, offsetY);
        contextRef.current.stroke();
        setHasDrawn(true);
    }, [isDrawing]);

    const handleEnd = useCallback(() => {
        if (!contextRef.current) return;
        contextRef.current.closePath();
        setIsDrawing(false);
        setHasDrawn(true);
    }, []);

    // Mouse event handlers
    const handleMouseStart = useCallback((event) => {
        handleStart(event.clientX, event.clientY);
    }, [handleStart]);

    const handleMouseMove = useCallback((event) => {
        handleMove(event.clientX, event.clientY);
    }, [handleMove]);

    const handleMouseEnd = useCallback(() => {
        handleEnd();
    }, [handleEnd]);

    // Touch event handlers
    const handleTouchStart = useCallback((event) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        const touch = event.touches[0];
        handleStart(touch.clientX, touch.clientY);
    }, [handleStart]);

    const handleTouchMove = useCallback((event) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        const touch = event.touches[0];
        handleMove(touch.clientX, touch.clientY);
    }, [handleMove]);

    const handleTouchEnd = useCallback((event) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        handleEnd();
    }, [handleEnd]);

    // Set up touch event listeners
    useEffect(() => {
        if (!isForSignature || !canvasRef.current) return;

        const canvas = canvasRef.current;
        
        canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
        canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });

        return () => {
            canvas.removeEventListener('touchstart', handleTouchStart);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', handleTouchEnd);
            canvas.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [isForSignature, handleTouchStart, handleTouchMove, handleTouchEnd]);

    const clearCanvas = () => {
        if (!canvasRef.current) return;
        
        const canvas = canvasRef.current;
        // Use the existing context reference instead of creating a new one
        const context = contextRef.current;
        
        if (!context) return;
        
        // Save the current transformation
        context.save();
        
        // Reset the transformation to clear the entire canvas
        context.setTransform(1, 0, 0, 1, 0, 0);
        
        // Clear the entire canvas
        context.clearRect(0, 0, canvas.width, canvas.height);
        
        // Fill with white background to prevent black artifacts
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Restore the transformation (with the device pixel ratio scaling)
        context.restore();
        
        // Reset drawing state
        setIsDrawing(false);
        setHasDrawn(false);
        
        // Visual feedback that the signature was cleared
        ToastMessage.success("Signature cleared", 1, { fontSize: '14px' });
    };

    // Check if canvas has actual drawing content
    const isCanvasEmpty = (canvas) => {
        const context = canvas.getContext('2d');
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixelBuffer = new Uint32Array(imageData.data.buffer);
        
        // Check for any non-white pixels (any pixel that isn't 0xFFFFFFFF)
        // We're checking for any pixel that isn't completely white (255,255,255,255)
        return !pixelBuffer.some(color => color !== 0xFFFFFFFF);
    };

    const submitReceipt = () => {
        const canvas = canvasRef.current;
        
        // Check if canvas exists
        if (!canvas) {
            ToastMessage.error("Signature pad not initialized. Please try refreshing the page.", 4, { 
                fontSize: '16px',
                marginTop: '20px'
            });
            return;
        }

        // Check if user has drawn anything
        if (!hasDrawn) {
            ToastMessage.error("Please provide your signature before submitting the receipt", 4, { 
                fontSize: '16px',
                marginTop: '20px'
            });
            return;
        }

        // Check if canvas is actually empty (no signature drawn)
        if (isCanvasEmpty(canvas)) {
            ToastMessage.error("Your signature appears to be empty. Please draw your signature before submitting.", 4, { 
                fontSize: '16px',
                marginTop: '20px'
            });
            return;
        }
        
        const signatureImage = canvas.toDataURL("image/png");
        
        // Navigate to confirmation page with receipt details and signature data
        history.push({
            pathname: '/scholar-acknowledgement-confirmation',
            state: { 
                receiptDetails: {
                    id: receipt.id,
                    date: receipt.date,
                    type: receipt.type,
                    amount: receipt.amount,
                    scholarName: scholarName,
                    signatureData: signatureImage // Pass signature as base64
                }
            }
        });
    };

    const handleBack = () => {
        history.push('/scholar-acknowledgement-receipt');
    };

    const handleDownloadPDF = async () => {
        const receiptElement = document.querySelector('.scholar-ars-form');
        if (!receiptElement) return;
        
        try {
            // Temporarily hide all buttons in the signature section
            const allButtons = document.querySelectorAll('.scholar-ars-signature-section button');
            allButtons.forEach(btn => {
                btn.style.display = 'none';
            });

            const canvas = await html2canvas(receiptElement, {
                scale: 2,
                useCORS: true,
                logging: false
            });

            // Restore button visibility
            allButtons.forEach(btn => {
                btn.style.display = '';
            });

            const imgData = canvas.toDataURL('image/png');
            
            // Use a custom receipt size instead of A4 (approximately half the size)
            // Receipt size = 120mm x 180mm (portrait orientation)
            const pdf = new jsPDF('p', 'mm', [180, 120]);
            
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 0;

            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
            pdf.save(`scholarship_receipt_${receipt.date.replace(/[^0-9]/g, '')}.pdf`);
        } catch (error) {
            // console.error('Error generating PDF:', error);
            
            // Ensure buttons are restored even if there's an error
            const allButtons = document.querySelectorAll('.scholar-ars-signature-section button');
            allButtons.forEach(btn => {
                btn.style.display = '';
            });
            
            ToastMessage.error('Failed to generate PDF');
        }
    };

    if (!receipt) {
        return (
            <div className="page-wrapper">
                <Layout className="page-body">
                    <ScholarSidebar activePage="acknowledgement-receipt" />
                    <Content className="main-content">
                        <Card className="ars-form">
                            <Title level={4}>Receipt Not Found</Title>
                            <Text>The requested acknowledgement receipt details could not be found.</Text>
                            <Button 
                                type="primary" 
                                icon={<ArrowLeftOutlined />} 
                                onClick={handleBack}
                                style={{ marginTop: 16 }}
                            >
                                Back to Acknowledgement Receipts
                            </Button>
                        </Card>
                    </Content>
                </Layout>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <Layout className="page-body">
                <ScholarSidebar activePage="acknowledgement-receipt" />
                <Content className="main-content">
                    <div className="scholar-ars-back-button-container">
                        <Tooltip title="Back to Acknowledgement Receipts">
                            <Button 
                                type="default"
                                shape="circle"
                                icon={<ArrowLeftOutlined />}
                                onClick={handleBack}
                                className="scholar-ars-back-button"
                            />
                        </Tooltip>
                    </div>
                    <Card className='scholar-ars-form'>
                        <div className="scholar-ars-company-header" style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'center' : 'flex-start',
                            gap: isExtraSmallMobile ? '8px' : isSmallMobile ? '10px' : isMobile ? '12px' : '20px',
                            textAlign: isMobile ? 'center' : 'left',
                            marginBottom: '36px'
                        }}>
                            <img 
                                src={process.env.PUBLIC_URL + '/images/MYT Logo.png'} 
                                alt="MYT SoftDev Solutions, Inc." 
                                className="scholar-ars-company-logo"
                                style={{
                                    width: isExtraSmallMobile ? '85px' : isSmallMobile ? '90px' : isMobile ? '100px' : '180px',
                                    height: 'auto',
                                    marginRight: isMobile ? '0' : (isExtraSmallMobile ? '4px' : isSmallMobile ? '6px' : isMobile ? '8px' : '20px'),
                                    marginBottom: isMobile ? '8px' : '0'
                                }}
                            />
                            <div className="scholar-ars-company-info" style={{
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                <h1 className="scholar-ars-company-name" style={{
                                    fontSize: isExtraSmallMobile ? '16px' : isSmallMobile ? '17px' : isMobile ? '18px' : '22px',
                                    textAlign: isMobile ? 'center' : 'left',
                                    fontWeight: 600
                                }}>MYT SoftDev Solutions, Inc.</h1>
                                <p className="scholar-ars-company-address" style={{
                                    fontSize: isExtraSmallMobile ? '11px' : isSmallMobile ? '12px' : isMobile ? '13px' : '15px',
                                    lineHeight: isMobile ? '1.3' : '1.5',
                                    textAlign: isMobile ? 'center' : 'left',
                                    marginTop: '4px'
                                }}>301 The Greenery, Pope John Paul II Ave, Cebu City, 6000 Cebu</p>
                            </div>
                        </div>
                        <div className="scholar-ars-receipt-title">
                            <h2>ACKNOWLEDGEMENT RECEIPT</h2>
                        </div>
                        <div className="scholar-ars-receipt-content">
                            <p className="scholar-ars-receipt-text">
                                I, <span className="scholar-ars-scholar-inline">{scholarName}</span>, the undersigned scholar of MYT SoftDev Solutions, Inc., acknowledge the receipt of our <span className="scholar-ars-blank-line">{receipt.type}</span> worth <span style={{ fontWeight: 'bold' }}>₱{Number(receipt.amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> received on <span className="scholar-ars-blank-line">{formatDate(receipt.date)}</span>.
                            </p>
                        </div>
                        <div className="scholar-ars-signature-section">
                            <p className="scholar-ars-acknowledged-by">Acknowledged by:</p>
                            {isForSignature ? (
                                <div>
                                    <div className="scholar-ars-signature-box" style={{ 
                                        width: isMobile ? '160px' : '300px', 
                                        height: isMobile ? '160px' : '200px',
                                        aspectRatio: '1/1',
                                        position: 'relative'
                                    }}>
                                        <canvas
                                            ref={canvasRef}
                                            onMouseDown={handleMouseStart}
                                            onMouseUp={handleMouseEnd}
                                            onMouseMove={handleMouseMove}
                                            onMouseLeave={handleMouseEnd}
                                            style={{ 
                                                width: '100%', 
                                                height: '100%',
                                                maxHeight: isMobile ? '160px' : '200px',
                                                maxWidth: isMobile ? '160px' : '300px',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                cursor: 'crosshair',
                                                touchAction: 'none',
                                                backgroundColor: '#ffffff',
                                                WebkitTapHighlightColor: 'transparent',
                                                WebkitTouchCallout: 'none',
                                                WebkitUserSelect: 'none',
                                                userSelect: 'none',
                                                display: 'block',
                                                msTouchAction: 'none',
                                                borderRadius: '4px',
                                                transform: 'translateZ(0)',
                                                WebkitTransform: 'translateZ(0)',
                                                backfaceVisibility: 'hidden',
                                                WebkitBackfaceVisibility: 'hidden',
                                                willChange: 'transform'
                                            }}
                                        />
                                    </div>
                                    <div className="scholar-ars-name-container" style={{ 
                                        width: isMobile ? '160px' : '300px'
                                    }}>
                                        <p className="scholar-ars-scholar-name">{scholarName}</p>
                                        <p className="scholar-ars-scholar-title">Scholar</p>
                                    </div>
                                    <div className="scholar-ars-signature-actions">
                                        <Button 
                                            type="primary"
                                            onClick={submitReceipt}
                                            style={{ minWidth: '140px', height: '40px' }} 
                                        >
                                            Submit Receipt
                                        </Button>
                                        <Button 
                                            type="default" 
                                            onClick={clearCanvas}
                                            className="scholar-ars-clear-btn"
                                            style={{ 
                                                minWidth: '100px', 
                                                height: '40px',
                                                backgroundColor: '#fff8f8',
                                                borderColor: '#ff4d4f',
                                                color: '#ff4d4f'
                                            }} 
                                            icon={<span role="img" aria-label="eraser" className="anticon anticon-delete">
                                                <svg viewBox="64 64 896 896" focusable="false" data-icon="delete" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                                                    <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                                                </svg>
                                            </span>}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                    <p style={{ 
                                        textAlign: 'left',
                                        marginTop: '10px',
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        {isMobile ? 
                                            "Draw your signature in the box above using your finger or stylus" : 
                                            "Please use your mouse or touch screen to sign"}
                                    </p>
                                </div>
                            ) : isSigned && receipt.signature ? (
                                <div>
                                    <div className="scholar-ars-signature-box" style={{ 
                                        width: isMobile ? '160px' : '300px', 
                                        height: isMobile ? '160px' : '200px',
                                        aspectRatio: '1/1',
                                        position: 'relative'
                                    }}>
                                        <img 
                                            src={receipt.signature}
                                            alt="Digital Signature"
                                            className="scholar-ars-signature-image"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </div>
                                    <div className="scholar-ars-name-container" style={{ 
                                        width: isMobile ? '160px' : '300px'
                                    }}>
                                        <p className="scholar-ars-scholar-name">{scholarName}</p>
                                        <p className="scholar-ars-scholar-title">Scholar</p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="scholar-ars-signature-box" style={{ 
                                        width: isMobile ? '160px' : '300px', 
                                        height: isMobile ? '160px' : '200px',
                                        aspectRatio: '1/1',
                                        position: 'relative'
                                    }}></div>
                                    <div className="scholar-ars-name-container" style={{ 
                                        width: isMobile ? '160px' : '300px'
                                    }}>
                                        <p className="scholar-ars-scholar-name">{scholarName}</p>
                                        <p className="scholar-ars-scholar-title">Scholar</p>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <Tooltip 
                                    title={isForSignature ? "Receipt must be signed before downloading" : "Download this receipt as PDF"}
                                >
                                    <Button 
                                        type="primary"
                                        icon={<FileTextOutlined />}
                                        onClick={handleDownloadPDF}
                                        disabled={isForSignature}
                                    >
                                        Download Receipt
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </Card>
                    
                    <div className="scholar-ars-company-branding" style={{ textAlign: 'center' }}>
                        <Text className="scholar-ars-company-text">©2025 MYT Softdev Solutions Inc.</Text>
                    </div>
                </Content>
            </Layout>
        </div>
    );
};

export default ScholarAcknowledgementDetails; 